import { useEffect, useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import parkingYardService from "../../../services/parkingYard.service";
import DownloadSampleExcelButton from "../../../components/common/DownloadSampleExcelButton";
import { saveExcelBlob, getApiErrorMessage } from "../../../utils/downloadExcel";

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ParkingYardUploadModal({
  open,
  bankName,
  branchName,
  onClose,
  onSuccess,
  onStatusChange,
}) {
  const inputRef = useRef(null);
  const uploadInFlight = useRef(false);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!open) return;
    uploadInFlight.current = false;
    setFile(null);
    setDragOver(false);
    setUploading(false);
    setError("");
    setSummary(null);
    if (inputRef.current) inputRef.current.value = "";
  }, [open, bankName, branchName]);

  if (!open) return null;

  const acceptFile = (next) => {
    if (!next || uploading) return;
    const name = String(next.name || "").toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(name)) {
      setError("Only .xlsx, .xls, and .csv files are allowed.");
      return;
    }
    setError("");
    setSummary(null);
    setFile(next);
  };

  const handleSample = async () => {
    try {
      const response = await parkingYardService.downloadSample();
      await saveExcelBlob(response, "parking-yard-sample.xlsx");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Failed to download sample Excel."));
    }
  };

  const handleUpload = async () => {
    if (uploadInFlight.current || uploading || !file) return;
    uploadInFlight.current = true;
    setUploading(true);
    setError("");
    setSummary(null);
    onStatusChange?.("processing");

    try {
      const res = await parkingYardService.upload(file, { bankName, branchName });
      const payload = res?.data?.data || res?.data || {};
      setSummary(payload);
      const totalRecords =
        payload.totalRows != null
          ? Number(payload.totalRows)
          : Number(payload.inserted || 0) + Number(payload.updated || 0);
      onSuccess?.({
        totalRecords,
        inserted: payload.inserted,
        updated: payload.updated,
        skipped: payload.skipped,
        errors: payload.errors,
        uploadedBy: payload.uploadedBy || null,
      });
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      onStatusChange?.("failed");
      setError(await getApiErrorMessage(err, "Parking yard upload failed."));
    } finally {
      uploadInFlight.current = false;
      setUploading(false);
    }
  };

  return (
    <div
      className="modal-overlay bd-upload-modal-overlay"
      role="presentation"
      onClick={uploading ? undefined : onClose}
    >
      <div
        className="modal-box bd-upload-modal bd-upload-modal--compact py-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="py-upload-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="py-upload-modal-title">Upload Parking Yard</h3>
        <p className="modal-subtitle py-selected-meta">
          <span>
            <strong>Bank:</strong> {bankName}
          </span>
          <span>
            <strong>Branch:</strong> {branchName}
          </span>
        </p>

        <DownloadSampleExcelButton
          showFieldSelect={false}
          className="bd-sample-excel-btn"
          label="Download sample Excel"
          disabled={uploading}
          onDownload={handleSample}
        />

        <div
          className={`py-drop${dragOver ? " py-drop--over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (uploading) return;
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            acceptFile(e.dataTransfer.files?.[0]);
          }}
        >
          <FiUpload size={16} />
          <p>
            <strong>Drag &amp; Drop Excel File</strong>
            <span>or</span>
            <button
              type="button"
              className="secondary-page-btn"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              Choose File
            </button>
          </p>
          <span className="muted">Supported: XLSX, XLS</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            disabled={uploading}
            onChange={(e) => acceptFile(e.target.files?.[0])}
          />
        </div>

        {file ? (
          <p className="py-file">
            {file.name} · {formatBytes(file.size)}
          </p>
        ) : null}

        <button
          type="button"
          className="bd-btn bd-btn-primary"
          disabled={!file || uploading}
          onClick={handleUpload}
        >
          {uploading ? "Uploading…" : "Upload Parking Yard"}
        </button>

        {error ? <p className="bd-feedback bd-error">{error}</p> : null}

        {summary ? (
          <div className="py-summary">
            <h3>Parking Yard Upload Completed</h3>
            <ul>
              <li>
                <span>Total Rows</span>
                <strong>{Number(summary.totalRows || 0).toLocaleString("en-IN")}</strong>
              </li>
              <li>
                <span>Inserted</span>
                <strong>{Number(summary.inserted || 0).toLocaleString("en-IN")}</strong>
              </li>
              <li>
                <span>Updated</span>
                <strong>{Number(summary.updated || 0).toLocaleString("en-IN")}</strong>
              </li>
              <li>
                <span>Skipped</span>
                <strong>{Number(summary.skipped || 0).toLocaleString("en-IN")}</strong>
              </li>
              <li>
                <span>Errors</span>
                <strong>{Number(summary.errors || 0).toLocaleString("en-IN")}</strong>
              </li>
            </ul>
            {Array.isArray(summary.errorRows) && summary.errorRows.length ? (
              <div className="py-errors">
                {summary.errorRows.map((row) => (
                  <p key={`${row.row}-${row.reason}`}>
                    Row {row.row} - {row.reason}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="modal-actions">
          <button
            type="button"
            className="secondary-page-btn"
            onClick={onClose}
            disabled={uploading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
